import React, { useState, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Typography,
  alpha,
  Tooltip,
  LinearProgress,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useTranslation } from 'react-i18next';

// VN Market color conventions
import { getScoreColor } from '../../theme';

const VN_COLORS = {
  ceiling: '#c800c8',   // purple - ceiling price
  floor: '#0080ff',     // blue - floor price
  up: '#00b14f',        // green - price up
  down: '#e14040',      // red - price down
  ref: '#f5a623',       // yellow - reference price
  muted: '#64748b',
  border: '#e2e8f0',
};

const getPriceColor = (pct) => {
  if (pct == null) return VN_COLORS.muted;
  if (pct >= 6.5) return VN_COLORS.ceiling;
  if (pct <= -6.5) return VN_COLORS.floor;
  if (pct > 0) return VN_COLORS.up;
  if (pct < 0) return VN_COLORS.down;
  return VN_COLORS.ref;
};

const getRowBg = (pct) => {
  if (pct == null) return 'transparent';
  if (pct >= 6.5) return alpha(VN_COLORS.ceiling, 0.04);
  if (pct <= -6.5) return alpha(VN_COLORS.floor, 0.04);
  if (pct > 0) return alpha(VN_COLORS.up, 0.02);
  if (pct < 0) return alpha(VN_COLORS.down, 0.02);
  return alpha(VN_COLORS.ref, 0.02);
};

const StocksView = ({
  stocks,
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onStockClick
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [orderBy, setOrderBy] = useState(null);
  const [order, setOrder] = useState('asc');

  const handleSort = (columnId) => {
    if (orderBy === columnId) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(columnId);
      setOrder(['market_cap_bn', 'change_pct', 'overall_val_score', 'roe', 'volume'].includes(columnId) ? 'desc' : 'asc');
    }
  };

  const sortedStocks = useMemo(() => {
    if (!orderBy) return stocks;
    return [...stocks].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === 'string') {
        const cmp = aVal.localeCompare(bVal, 'vi');
        return order === 'asc' ? cmp : -cmp;
      }
      const cmp = aVal - bVal;
      return order === 'asc' ? cmp : -cmp;
    });
  }, [stocks, orderBy, order]);

  const formatMarketCap = (val) => {
    if (val == null) return '-';
    if (val >= 1000) return `${(val / 1000).toFixed(1)}T`;
    return `${val.toFixed(0)}B`;
  };

  const formatVolume = (vol) => {
    if (vol == null) return '-';
    if (vol >= 1_000_000) return `${(vol / 1_000_000).toFixed(1)}M`;
    if (vol >= 1_000) return `${(vol / 1_000).toFixed(0)}K`;
    return vol.toLocaleString('vi-VN');
  };

  const columns = [
    { id: 'ticker', label: t('vnstock.stocks.symbol'), align: 'left', w: 170, sortable: true },
    ...(isMobile ? [] : [{ id: 'exchange', label: t('vnstock.stocks.exchange'), align: 'center', w: 60, sortable: true }]),
    { id: 'latest_price', label: t('vnstock.stocks.price'), align: 'right', w: 80, sortable: true },
    { id: 'change_pct', label: '+/-%', align: 'right', w: 72, sortable: true },
    { id: 'overall_val_score', label: t('vnstock.stocks.valuation'), align: 'center', w: 90, sortable: true },
    { id: 'market_cap_bn', label: t('vnstock.stocks.marketCap'), align: 'right', w: 80, sortable: true },
    { id: 'pe', label: 'P/E', align: 'right', w: 55, sortable: true },
    { id: 'pb', label: 'P/B', align: 'right', w: 55, sortable: true },
    { id: 'roe', label: 'ROE', align: 'right', w: 60, sortable: true },
    ...(isMobile ? [] : [
      { id: 'eps', label: 'EPS', align: 'right', w: 70, sortable: true },
      { id: 'volume', label: t('vnstock.stocks.volume'), align: 'right', w: 80, sortable: true },
    ]),
  ];

  const cellSx = { py: 0.7, px: 1, borderBottom: '1px solid #f1f5f9' };
  const headCellSx = {
    ...cellSx, py: 0.8, fontWeight: 700, fontSize: '0.72rem', color: '#94a3b8',
    bgcolor: '#fafbfc', borderBottom: '1px solid #e2e8f0',
    letterSpacing: '0.02em', whiteSpace: 'nowrap', userSelect: 'none',
  };

  return (
    <>
      <TableContainer>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.id} align={col.align}
                  sortDirection={orderBy === col.id ? order : false}
                  sx={{ ...headCellSx, width: col.w }}>
                  {col.sortable ? (
                    <TableSortLabel active={orderBy === col.id}
                      direction={orderBy === col.id ? order : 'asc'}
                      onClick={() => handleSort(col.id)}
                      sx={{ '&.MuiTableSortLabel-root': { color: '#94a3b8', '&:hover': { color: '#475569' } },
                        '&.Mui-active': { color: '#475569', '& .MuiTableSortLabel-icon': { color: '#475569' } } }}>
                      {col.label}
                    </TableSortLabel>
                  ) : col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedStocks.map((stock) => {
              const priceColor = getPriceColor(stock.change_pct);
              const scoreColor = getScoreColor(stock.overall_val_score_level);
              const score = stock.overall_val_score;

              return (
                <TableRow key={stock.ticker} onClick={() => onStockClick?.(stock.ticker)}
                  sx={{ cursor: 'pointer', bgcolor: getRowBg(stock.change_pct),
                    transition: 'background-color 0.1s',
                    '&:hover': { bgcolor: alpha('#667eea', 0.06), '& .ticker-cell': { color: '#667eea' } } }}>

                  {/* Ticker + Company */}
                  <TableCell sx={{ ...cellSx, pl: 1.5 }}>
                    <Box sx={{ overflow: 'hidden' }}>
                      <Typography className="ticker-cell" sx={{
                        fontWeight: 800, color: '#0f172a', fontSize: '0.82rem',
                        letterSpacing: '0.5px', lineHeight: 1.3, transition: 'color 0.1s' }}>
                        {stock.ticker}
                      </Typography>
                      {!isMobile && (
                        <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 500,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          lineHeight: 1.2, maxWidth: 160 }}>
                          {stock.company_name || ''}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>

                  {/* Exchange */}
                  {!isMobile && (
                    <TableCell align="center" sx={cellSx}>
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, lineHeight: 1,
                        color: stock.exchange === 'HOSE' ? '#dc2626' : stock.exchange === 'HNX' ? '#2563eb' : '#16a34a' }}>
                        {stock.exchange}
                      </Typography>
                    </TableCell>
                  )}

                  {/* Price */}
                  <TableCell align="right" sx={cellSx}>
                    <Typography sx={{ fontWeight: 800, color: priceColor, fontSize: '0.85rem',
                      fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
                      {stock.latest_price != null ? stock.latest_price.toLocaleString('vi-VN') : '-'}
                    </Typography>
                  </TableCell>

                  {/* Change % with colored pill */}
                  <TableCell align="right" sx={cellSx}>
                    {stock.change_pct != null ? (
                      <Box sx={{ display: 'inline-flex', alignItems: 'center',
                        bgcolor: alpha(priceColor, 0.1), borderRadius: 1, px: 0.5, py: 0.1,
                        minWidth: 58, justifyContent: 'flex-end' }}>
                        {stock.change_pct > 0 ? (
                          <ArrowDropUpIcon sx={{ fontSize: 16, color: priceColor, ml: -0.3 }} />
                        ) : stock.change_pct < 0 ? (
                          <ArrowDropDownIcon sx={{ fontSize: 16, color: priceColor, ml: -0.3 }} />
                        ) : null}
                        <Typography sx={{ fontWeight: 700, color: priceColor, fontSize: '0.78rem',
                          fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                          {stock.change_pct > 0 ? '+' : ''}{stock.change_pct.toFixed(2)}%
                        </Typography>
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>-</Typography>
                    )}
                  </TableCell>

                  {/* Valuation Score - Progress Bar */}
                  <TableCell align="center" sx={{ ...cellSx, px: 0.75 }}>
                    {score != null ? (
                      <Tooltip title={`Score: ${Math.round(score)}/100`} placement="top" arrow>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ flex: 1, position: 'relative' }}>
                            <LinearProgress variant="determinate" value={Math.min(score, 100)}
                              sx={{ height: 6, borderRadius: 3, bgcolor: alpha(scoreColor, 0.08),
                                '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: scoreColor } }} />
                          </Box>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', color: scoreColor,
                            minWidth: 22, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            {Math.round(score)}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1' }}>-</Typography>
                    )}
                  </TableCell>

                  {/* Market Cap */}
                  <TableCell align="right" sx={cellSx}>
                    <Typography sx={{ color: '#334155', fontSize: '0.78rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {formatMarketCap(stock.market_cap_bn)}
                    </Typography>
                  </TableCell>

                  {/* PE */}
                  <TableCell align="right" sx={cellSx}>
                    <Typography sx={{ fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums',
                      color: stock.pe != null && stock.pe < 10 ? '#16a34a' : stock.pe != null && stock.pe > 30 ? '#dc2626' : '#475569',
                      fontWeight: stock.pe != null && (stock.pe < 10 || stock.pe > 30) ? 700 : 500 }}>
                      {stock.pe != null ? stock.pe.toFixed(1) : '-'}
                    </Typography>
                  </TableCell>

                  {/* PB */}
                  <TableCell align="right" sx={cellSx}>
                    <Typography sx={{ fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums',
                      color: stock.pb != null && stock.pb < 1 ? '#16a34a' : stock.pb != null && stock.pb > 3 ? '#dc2626' : '#475569',
                      fontWeight: stock.pb != null && (stock.pb < 1 || stock.pb > 3) ? 700 : 500 }}>
                      {stock.pb != null ? stock.pb.toFixed(2) : '-'}
                    </Typography>
                  </TableCell>

                  {/* ROE with highlight */}
                  <TableCell align="right" sx={cellSx}>
                    {stock.roe != null ? (
                      <Box sx={{ display: 'inline-flex', alignItems: 'center',
                        bgcolor: stock.roe > 0.15 ? alpha('#22c55e', 0.08) : stock.roe > 0.10 ? alpha('#f59e0b', 0.06) : 'transparent',
                        borderRadius: 1, px: 0.5 }}>
                        <Typography sx={{ fontWeight: stock.roe > 0.15 ? 700 : 500,
                          color: stock.roe > 0.15 ? '#16a34a' : stock.roe > 0.10 ? '#d97706' : '#475569',
                          fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums' }}>
                          {(stock.roe * 100).toFixed(1)}%
                        </Typography>
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: '0.78rem', color: '#cbd5e1' }}>-</Typography>
                    )}
                  </TableCell>

                  {/* EPS */}
                  {!isMobile && (
                    <TableCell align="right" sx={cellSx}>
                      <Typography sx={{ color: '#475569', fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums',
                        fontWeight: stock.eps != null && stock.eps > 5000 ? 600 : 400 }}>
                        {stock.eps != null ? stock.eps.toLocaleString('vi-VN', { maximumFractionDigits: 0 }) : '-'}
                      </Typography>
                    </TableCell>
                  )}

                  {/* Volume */}
                  {!isMobile && (
                    <TableCell align="right" sx={cellSx}>
                      <Typography sx={{ color: '#64748b', fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
                        {formatVolume(stock.volume)}
                      </Typography>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            
            {sortedStocks.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                    {t('common.noData', 'No data')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ borderTop: '1px solid #e5e7eb', bgcolor: '#fafbfc', flexShrink: 0 }}>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100, 200]}
          component="div" count={total} rowsPerPage={rowsPerPage} page={page}
          onPageChange={onPageChange} onRowsPerPageChange={onRowsPerPageChange}
          labelRowsPerPage={t('vnstock.stocks.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) =>
            t('vnstock.stocks.displayedRows', { from, to, count: count !== -1 ? count.toLocaleString() : `> ${to}` })}
          sx={{ '& .MuiTablePagination-toolbar': { px: 2, minHeight: 44 },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.8rem' } }}
        />
      </Box>
    </>
  );
};

export default StocksView;